// src/pages/Dashboard.jsx
// Premium, focused Dashboard (time-only filters)
// UI/UX refactor: single-elevation shell, stronger hierarchy, simplified filter,
// donut with center total, higher-contrast A/R, consistent iconography & motion.

import React, { useMemo, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Tooltip as ChartTooltip,
  Legend,
} from 'chart.js';
import { Line, Doughnut, Bar } from 'react-chartjs-2';
import { useQuery } from '@tanstack/react-query';
import { useDashboard } from '../hooks/useDashboardSummary';
import { toSearchParams } from '../services/dashboardService';
import api from '../api/axios';
import { Activity, CalendarRange, RefreshCcw, ChevronDown } from 'lucide-react';

// ✅ unified money helpers (same as invoices/quotes)
import { formatMoney, safeCurrency } from '../utils/money';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  ChartTooltip,
  Legend
);

/* ───────────────────────── Utils ───────────────────────── */
const cls = (...xs) =>
  xs
    .flatMap((x) =>
      typeof x === 'string'
        ? x
        : x && typeof x === 'object'
        ? Object.keys(x).filter((k) => x[k])
        : []
    )
    .join(' ')
    .trim();

const fmtMonth = (yyyyMM) => {
  if (!yyyyMM) return '—';
  const [y, m] = String(yyyyMM).split('-').map(Number);
  const d = new Date(Date.UTC(y || 1970, (m || 1) - 1, 1));
  return d.toLocaleDateString(undefined, {
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
};

const safeNum = (v, d = 0) => (Number.isFinite(v) ? v : d);
const pct = (v) => `${Math.round(safeNum(v) * 100)}%`;

// TZ-aware date label for upcoming jobs
const dateOnly = (d, tz = 'UTC') => {
  try {
    if (!d) return '—';
    const x = new Date(d);
    return x.toLocaleDateString(undefined, { timeZone: tz });
  } catch {
    return '—';
  }
};

/* ───────────────────────── Tooltip + Scope chips ───────────────────────── */
function InfoTip({ text, label = 'Info' }) {
  return (
    <span className="relative inline-flex items-center" aria-label={label} title={text}>
      <span
        className="group inline-flex h-5 w-5 cursor-help items-center justify-center rounded-full border border-slate-200 bg-slate-50 text-[11px] font-bold text-slate-600"
        tabIndex={0}
      >
        i
        <span
          className={cls(
            'pointer-events-none absolute -top-2 left-1/2 z-50 hidden w-72 -translate-x-1/2 -translate-y-full',
            'rounded-md border border-slate-200 bg-white p-2 text-[11px] leading-4 text-slate-700 shadow-lg',
            'group-hover:block group-focus:block'
          )}
          role="tooltip"
        >
          {text}
        </span>
      </span>
    </span>
  );
}

function ScopeChip({ scope = 'Period' }) {
  const tone =
    scope === 'All-time' ? 'indigo' : scope === 'Snapshot' ? 'warning' : 'default';
  const map = {
    default: 'bg-slate-50 text-slate-600 border-slate-200/60',
    warning: 'bg-amber-50 text-amber-700 border-amber-200/60',
    indigo: 'bg-indigo-50 text-indigo-700 border-indigo-200/60',
  };
  return (
    <span
      className={cls(
        'inline-flex items-center rounded-md border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider',
        map[tone] || map.default
      )}
    >
      {scope}
    </span>
  );
}

/* ───────────────────────── Micro UI ───────────────────────── */
function Chip({ children, tone = 'default' }) {
  const map =
    {
      default: 'bg-slate-50 text-slate-600 border-slate-200/60',
      info: 'bg-blue-50 text-blue-700 border-blue-200/60',
      success: 'bg-emerald-50 text-emerald-700 border-emerald-200/60',
      warning: 'bg-amber-50 text-amber-700 border-amber-200/60',
      danger: 'bg-rose-50 text-rose-700 border-rose-200/60',
      indigo: 'bg-indigo-50 text-indigo-700 border-indigo-200/60',
    }[tone] || 'bg-slate-50 text-slate-600 border-slate-200/60';
  return (
    <span
      className={cls(
        'inline-flex items-center rounded-md border px-2 py-0.5 text-[11px] font-medium',
        map
      )}
    >
      {children}
    </span>
  );
}

function IconGhostBtn({ title, onClick, disabled, children }) {
  return (
    <button
      type="button"
      aria-label={title}
      title={title}
      onClick={onClick}
      disabled={disabled}
      className={cls(
        'inline-flex h-7 w-7 items-center justify-center rounded-md border border-slate-200/80 bg-white',
        'text-slate-700 transition-all hover:bg-slate-50 hover:border-slate-300 disabled:opacity-40 disabled:cursor-not-allowed'
      )}
    >
      {children}
    </button>
  );
}

function KPI({ label, value, hint, tone = 'default', scope = 'Period', tooltip }) {
  const accentColors = {
    default: 'text-slate-900',
    success: 'text-emerald-600',
    warning: 'text-amber-600',
    info: 'text-blue-600',
  }[tone];

  return (
    <div className="group relative rounded-2xl bg-white p-5 shadow-sm transition-transform duration-200 hover:-translate-y-0.5">
      <div className="mb-1 flex items-center justify-between">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
          {label}
        </div>
        <div className="flex items-center gap-2">
          <ScopeChip scope={scope} />
          {tooltip ? <InfoTip text={tooltip} label={`${label} info`} /> : null}
        </div>
      </div>
      <div className="flex flex-col space-y-1">
        <div
          className={cls(
            'text-[2.25rem] leading-none font-extrabold tracking-tight',
            accentColors
          )}
        >
          {value}
        </div>
        {hint ? <div className="text-xs text-slate-500">{hint}</div> : null}
      </div>
    </div>
  );
}

/** Specialized KPI card to show Scheduled vs In-Progress in one card */
function JobsKPI({ scheduled = 0, underway = 0 }) {
  const tooltip =
    'Snapshot: Scheduled = future-dated jobs from now. In Progress = live statuses (en route / in progress / on site).';
  return (
    <div className="group relative rounded-2xl bg-white p-5 shadow-sm transition-transform duration-200 hover:-translate-y-0.5">
      <div className="mb-1 flex items-center justify-between">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
          Jobs
        </div>
        <div className="flex items-center gap-2">
          <ScopeChip scope="Snapshot" />
          <InfoTip text={tooltip} label="Jobs info" />
        </div>
      </div>
      <div className="mt-2 grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
            Scheduled
          </div>
          <div className="text-2xl font-bold tracking-tight text-slate-900">
            {scheduled}
          </div>
        </div>
        <div className="space-y-1">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
            In Progress
          </div>
          <div className="text-2xl font-bold tracking-tight text-slate-900">
            {underway}
          </div>
        </div>
      </div>
    </div>
  );
}

function Section({ title, right, children, className = '', scope, tooltip }) {
  return (
    <section
      className={cls(
        'rounded-2xl bg-white p-6 sm:p-7 shadow-sm transition-transform duration-200 hover:-translate-y-0.5',
        className
      )}
    >
      <div className="mb-5 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h2 className="text-base font-semibold tracking-tight text-slate-900">
            {title}
          </h2>
          {scope ? <ScopeChip scope={scope} /> : null}
          {tooltip ? <InfoTip text={tooltip} label={`${title} info`} /> : null}
        </div>
        {right}
      </div>
      {children}
    </section>
  );
}

function Skeleton({ className = '' }) {
  return (
    <div className={cls('animate-pulse rounded-2xl bg-slate-100/80', className)} />
  );
}

/* ───────────────────────── Team Workload helpers ───────────────────────── */
const initials = (name = '') =>
  String(name)
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase())
    .join('') || '•';

function Avatar({ name, src }) {
  if (src) {
    return (
      <img
        src={src}
        alt={name}
        className="h-8 w-8 rounded-full object-cover ring-1 ring-slate-200/60"
      />
    );
  }
  return (
    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 text-[11px] font-bold text-slate-600 ring-1 ring-slate-200/60">
      {initials(name)}
    </div>
  );
}

const UNDERWAY_RE = /^(en[_ ]?route|in[_ ]?progress|on[_ ]?site)$/i;
const SCHEDULED_RE = /^(scheduled|confirmed)$/i;

function pickItems(payload) {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.items)) return payload.items;
  if (Array.isArray(payload.results)) return payload.results;
  if (Array.isArray(payload.docs)) return payload.docs;
  if (Array.isArray(payload.list)) return payload.list;
  if (payload.data) return pickItems(payload.data);
  return [];
}

function useAssigneeWorks({ assigneeId, tz }) {
  return useQuery({
    queryKey: ['dashboard.assigneeWorks', assigneeId, tz],
    enabled: !!assigneeId,
    staleTime: 20_000,
    cacheTime: 60_000,
    queryFn: async () => {
      if (!assigneeId) return { underway: [], upcoming: [] };

      const baseParams = {
        assignedTo: assigneeId,
        assigneesAny: assigneeId,
        assigneeId: assigneeId,
        assignees: assigneeId,
        assigned: assigneeId,
        limit: 200,
        tz,
        sort: 'schedule.start:asc',
        sortBy: 'schedule.start:asc',
        includeClient: 1,
      };

      const tryFetch = async (params) => {
        const { data } = await api.get('/works', { params });
        return pickItems(data);
      };

      let raw = [];
      try {
        raw = await tryFetch(baseParams);
      } catch {
        raw = [];
      }

      if (!Array.isArray(raw) || raw.length === 0) {
        try {
          const { data } = await api.get('/works', {
            params: {
              limit: 400,
              tz,
              sort: 'updatedAt:desc',
              includeClient: 1,
            },
          });
          raw = pickItems(data);
        } catch {
          raw = [];
        }
      }

      const normId = (v) => {
        if (!v) return null;
        if (typeof v === 'string') return v;
        if (typeof v === 'object')
          return String(v._id || v.id || v.value || '');
        return String(v);
      };
      const id = String(assigneeId);
      const items = raw.filter((w) => {
        if (normId(w?.assignedTo) === id) return true;
        if (
          Array.isArray(w?.assignees) &&
          w.assignees.some((a) => normId(a) === id)
        )
          return true;
        if (
          Array.isArray(w?.assigned) &&
          w.assigned.some((a) => normId(a) === id)
        )
          return true;
        return false;
      });

      const now = new Date();
      const startOfToday = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate()
      ).getTime();

      const underway = items
        .filter((w) => UNDERWAY_RE.test(String(w.status || '')))
        .sort(
          (a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0)
        )
        .slice(0, 6);

      const upcoming = items
        .filter((w) => {
          const s = String(w.status || '');
          const when = w?.schedule?.start
            ? new Date(w.schedule.start).getTime()
            : NaN;
          return (
            SCHEDULED_RE.test(s) &&
            Number.isFinite(when) &&
            when >= startOfToday
          );
        })
        .sort(
          (a, b) =>
            new Date(a.schedule.start) - new Date(b.schedule.start)
        )
        .slice(0, 8);

      return { underway, upcoming };
    },
  });
}

function TeamTabs({ team = [], tz = 'UTC', qsBase = '' }) {
  const rawMembers = (Array.isArray(team) ? team : []).filter(
    (t) => !!t.userId
  );

  const ids = Array.from(new Set(rawMembers.map((m) => m.userId)));
  const { data: usersMeta } = useQuery({
    queryKey: ['dashboard.usersMeta', ids.join(',')],
    enabled: ids.length > 0,
    staleTime: 60_000,
    cacheTime: 5 * 60_000,
    queryFn: async () => {
      try {
        // ✅ use /users/by-ids endpoint and id field (matches userController)
        const { data } = await api.get('/users/by-ids', {
          params: { ids: ids.join(',') },
        });
        const list = Array.isArray(data?.items)
          ? data.items
          : pickItems(data);
        const map = new Map();
        for (const u of list) {
          map.set(String(u.id || u._id), u);
        }
        return map;
      } catch {
        return new Map();
      }
    },
  });

  const members = rawMembers
    .filter((t) => {
      const meta = usersMeta?.get(String(t.userId));
      const status = String(meta?.status || t.status || '').toLowerCase();
      const inactiveFlag =
        meta?.active === false ||
        meta?.isActive === false ||
        t.active === false ||
        t.isActive === false ||
        ['inactive', 'disabled', 'archived'].includes(status) ||
        meta?.isDeleted ||
        !!meta?.deletedAt ||
        t.isDeleted ||
        !!t.deletedAt;
      return !inactiveFlag;
    })
    .sort((a, b) =>
      (a.name || '').localeCompare(b.name || '', undefined, {
        sensitivity: 'base',
      })
    );

  const [activeId, setActiveId] = useState(members[0]?.userId || null);

  useEffect(() => {
    if (!members.some((m) => m.userId === activeId)) {
      setActiveId(members[0]?.userId || null);
    }
  }, [members, activeId]);

  const { data, isLoading } = useAssigneeWorks({ assigneeId: activeId, tz });
  const underway = data?.underway || [];
  const upcoming = data?.upcoming || [];

  return (
    <Section
      title="Team Workload"
      scope="Snapshot"
      tooltip="Live operational view. Assignee tabs show jobs that are currently in progress and upcoming scheduled items. Not affected by the period filter."
      right={
        <Link
          className="text-xs font-medium text-slate-600 transition-colors hover:text-slate-900"
          to={`/work?${qsBase}`}
        >
          View All →
        </Link>
      }
    >
      {members.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-2xl bg-slate-50/50 py-10 text-center">
          <div className="text-4xl opacity-15">👥</div>
          <div className="mt-3 text-sm font-medium text-slate-700">
            No Assignments
          </div>
          <p className="mt-1 max-w-xs text-xs text-slate-500">
            Assign jobs and tasks to team members to track workload.
          </p>
        </div>
      ) : (
        <>
          <div className="mb-4 flex flex-wrap items-center gap-2">
            {members.map((m) => (
              <button
                key={m.userId}
                type="button"
                onClick={() => setActiveId(m.userId)}
                className={cls(
                  'flex items-center gap-2 rounded-md border px-3 py-1.5 text-xs font-medium shadow-sm transition-all',
                  activeId === m.userId
                    ? 'border-slate-900 bg-slate-900 text-white'
                    : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                )}
                title={m.name}
              >
                <Avatar name={m.name} src={m.avatarUrl} />
                <span className="truncate max-w-[140px]">{m.name}</span>
              </button>
            ))}
          </div>

          {isLoading ? (
            <div className="space-y-3">
              <Skeleton className="h-20" />
              <Skeleton className="h-20" />
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              <div className="relative rounded-2xl border border-slate-200 bg-white p-4">
                <div className="absolute left-0 top-0 h-full w-1.5 bg-emerald-500/80" />
                <div className="mb-3 flex items-center justify-between">
                  <div className="text-sm font-semibold text-slate-900">
                    In Progress
                  </div>
                  <Chip tone="success">{underway.length}</Chip>
                </div>
                {underway.length === 0 ? (
                  <div className="rounded-md border border-slate-200 bg-white p-4 text-center text-xs text-slate-500">
                    Nothing in progress.
                  </div>
                ) : (
                  <ul className="divide-y divide-slate-100 rounded-md">
                    {underway.map((w) => (
                      <li
                        key={w._id}
                        className="flex items-center justify-between px-2 py-3"
                      >
                        <div className="min-w-0">
                          <div className="truncate text-sm font-medium text-slate-900">
                            {(w.workNumber || w.code || w.shortId || '')
                              .toString()
                              .trim()}{' '}
                            • {w.title || '(Untitled)'}
                          </div>
                          <div className="mt-0.5 text-xs text-slate-600">
                            {w.client?.name || w.clientName || '—'} •{' '}
                            {String(w.status || '')
                              .replace(/_/g, ' ')
                              .trim()}
                          </div>
                        </div>
                        <Link
                          to={`/work/${w._id}`}
                          className="ml-4 shrink-0 rounded-md bg-slate-900 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-black"
                        >
                          Open →
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="relative rounded-2xl border border-slate-200 bg-white p-4">
                <div className="absolute left-0 top-0 h-full w-1.5 bg-indigo-500/80" />
                <div className="mb-3 flex items-center justify-between">
                  <div className="text-sm font-semibold text-slate-900">
                    Upcoming
                  </div>
                  <Chip tone="indigo">{upcoming.length}</Chip>
                </div>
                {upcoming.length === 0 ? (
                  <div className="rounded-md border border-slate-200 bg-white p-4 text-center text-xs text-slate-500">
                    No scheduled items.
                  </div>
                ) : (
                  <ul className="divide-y divide-slate-100 rounded-md">
                    {upcoming.map((w) => (
                      <li
                        key={w._id}
                        className="flex items-center justify-between px-2 py-3"
                      >
                        <div className="min-w-0">
                          <div className="truncate text-sm font-medium text-slate-900">
                            {(w.workNumber || w.code || w.shortId || '')
                              .toString()
                              .trim()}{' '}
                            • {w.title || '(Untitled)'}
                          </div>
                          <div className="mt-0.5 text-xs text-slate-600">
                            {w.client?.name || w.clientName || '—'} •{' '}
                            {dateOnly(w?.schedule?.start, tz)}
                          </div>
                        </div>
                        <Link
                          to={`/work/${w._id}`}
                          className="ml-4 shrink-0 rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-900 hover:bg-slate-50"
                        >
                          Open →
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </Section>
  );
}

/* ───────────────────────── Compact Time Filter Toolbar ───────────────────────── */
function CompactTimeFilter({ params, helpers, loading, onRefresh }) {
  const activeRange = params.range || '12m';
  const setQuick = (m) => helpers.setQuickMonths(m);
  const [showCustom, setShowCustom] = useState(false);
  const setFrom = (v) => helpers.setDateRange(v, params.to || '');
  const setTo = (v) => helpers.setDateRange(params.from || '', v);

  return (
    <div className="relative inline-flex items-center gap-1.5 rounded-md border border-slate-200/60 bg-white px-2.5 py-1.5 shadow-sm">
      <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
        Period
      </span>

      {['3m', '6m', '12m', '24m'].map((r) => {
        const on = activeRange === r && !params.from && !params.to;
        return (
          <button
            key={r}
            type="button"
            onClick={() => setQuick(Number(r.replace('m', '')))}
            className={cls(
              'rounded-md px-2.5 py-1 text-[11px] font-medium transition-all',
              on
                ? 'bg-slate-900 text-white shadow-sm'
                : 'bg-transparent text-slate-600 hover:bg-slate-50'
            )}
          >
            {r}
          </button>
        );
      })}

      <div className="mx-2 h-4 w-px bg-slate-200/60" />

      <button
        type="button"
        onClick={() => setShowCustom((s) => !s)}
        className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-medium text-slate-700 hover:bg-slate-50"
        title="Custom range"
      >
        <CalendarRange className="h-3.5 w-3.5" />
        Custom
        <ChevronDown className="h-3.5 w-3.5 opacity-60" />
      </button>

      <div className="ml-1.5 flex items-center gap-1">
        <button
          type="button"
          onClick={() => helpers.resetToLast12Months()}
          className="text-[11px] font-medium text-slate-600 hover:text-slate-900"
          title="Reset to last 12 months"
        >
          Reset
        </button>
        <IconGhostBtn title="Refresh" onClick={onRefresh} disabled={loading}>
          <RefreshCcw
            className={cls('h-3.5 w-3.5', loading && 'animate-spin')}
          />
        </IconGhostBtn>
      </div>

      {/* Popover */}
      {showCustom && (
        <>
          <button
            type="button"
            aria-label="Close"
            className="fixed inset-0 z-[40] cursor-default"
            onClick={() => setShowCustom(false)}
          />
          <div className="absolute right-0 top-full z-[50] mt-2 w-[360px] rounded-lg border border-slate-200 bg-white p-3 shadow-xl">
            <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
              Custom range
            </div>
            <div className="grid grid-cols-2 gap-2">
              <label className="text-[10px] font-medium text-slate-500">
                From
                <input
                  type="date"
                  className="mt-1 h-8 w-full rounded-md border border-slate-200 bg-slate-50 px-2 text-[11px] text-slate-700 focus:border-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-400/20"
                  value={params.from || ''}
                  onChange={(e) => setFrom(e.target.value)}
                />
              </label>
              <label className="text-[10px] font-medium text-slate-500">
                To
                <input
                  type="date"
                  className="mt-1 h-8 w-full rounded-md border border-slate-200 bg-slate-50 px-2 text-[11px] text-slate-700 focus:border-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-400/20"
                  value={params.to || ''}
                  onChange={(e) => setTo(e.target.value)}
                />
              </label>
            </div>
            <div className="mt-3 flex items-center justify-between">
              <button
                type="button"
                onClick={() => {
                  helpers.resetToLast12Months();
                  setShowCustom(false);
                }}
                className="text-[11px] font-medium text-slate-600 hover:text-slate-900"
              >
                Clear
              </button>
              <button
                type="button"
                onClick={() => setShowCustom(false)}
                className="rounded-md bg-slate-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-black"
              >
                Done
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/* ───────────────────────── Page ───────────────────────── */
export default function Dashboard() {
  const { params, filtersHelpers, summary } = useDashboard({
    urlSync: true,
    initialFilters: { range: '12m' },
    queryOptions: {
      staleTime: 30_000,
      cacheTime: 5 * 60_000,
      retry: 1,
      keepPreviousData: true,
      refetchOnWindowFocus: false,
    },
  });

  const { data, isLoading, isError, isFetching, refresh } = summary;

  // ✅ currency + timezone derived from backend (with safe fallbacks)
  const currency = safeCurrency(
    data?.currency ||
      data?.companyCurrency ||
      data?.filters?.currency ||
      data?.company?.billingCurrency ||
      data?.company?.defaultInvoiceCurrency ||
      'USD'
  );
  const tz = data?.filters?.tz || 'UTC';

  const k = data?.kpis || {};
  const jobsScheduled = safeNum(k.jobsScheduled);
  const jobsUnderway = safeNum(k.jobsInProgress);

  const kpis = useMemo(
    () => [
      {
        label: 'Cash Collected',
        value: formatMoney(safeNum(k.cashCollected), currency),
        tone: 'success',
        scope: 'Period',
        tooltip: 'Total payments received within the selected period.',
      },
      {
        label: 'Open A/R',
        value: formatMoney(safeNum(k.openAR), currency),
        tone: 'warning',
        scope: 'Snapshot',
        tooltip:
          'Unpaid invoice balance as of now. Excludes voided or written-off invoices.',
      },
      {
        label: 'Quotes Awaiting',
        value: safeNum(k.quotesAwaiting).toLocaleString(),
        tone: 'info',
        scope: 'Snapshot',
        tooltip:
          'Count of quotes currently in “sent” status and not expired yet.',
      },
      {
        label: 'Win Rate',
        value: pct(k.winRate),
        tone: 'success',
        scope: 'Period',
        tooltip:
          'Accepted ÷ (Accepted + Declined + Expired + Canceled) for quotes issued within the selected period.',
      },
    ],
    [k, currency]
  );

  // Revenue Trend
  const trend = Array.isArray(data?.revenueTrend) ? data.revenueTrend : [];
  const revLabels = trend.map((m) => fmtMonth(m.month));
  const bookedSeries = trend.map((m) => safeNum(m.booked));
  const collectedSeries = trend.map((m) => safeNum(m.collected));
  const lineData = useMemo(
    () => ({
      labels: revLabels,
      datasets: [
        {
          label: 'Booked',
          data: bookedSeries,
          borderColor: '#6366f1',
          backgroundColor: 'rgba(99,102,241,0.10)',
          tension: 0.4,
          pointRadius: 2.5,
          pointBackgroundColor: '#6366f1',
          fill: true,
          borderWidth: 2,
        },
        {
          label: 'Collected',
          data: collectedSeries,
          borderColor: '#10b981',
          backgroundColor: 'rgba(16,185,129,0.12)',
          tension: 0.4,
          pointRadius: 2.5,
          pointBackgroundColor: '#10b981',
          fill: true,
          borderWidth: 2,
        },
      ],
    }),
    [revLabels, bookedSeries, collectedSeries]
  );
  const lineOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'bottom',
          labels: {
            boxWidth: 10,
            padding: 12,
            font: { size: 11, weight: '500' },
            usePointStyle: true,
            pointStyle: 'circle',
          },
        },
        tooltip: {
          backgroundColor: 'rgba(15, 23, 42, 0.95)',
          titleColor: '#f1f5f9',
          bodyColor: '#e2e8f0',
          borderColor: 'rgba(148, 163, 184, 0.2)',
          borderWidth: 1,
          padding: 12,
          displayColors: true,
          boxPadding: 4,
          callbacks: {
            label: (ctx) =>
              `${ctx.dataset.label}: ${formatMoney(ctx.parsed.y, currency)}`,
          },
        },
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: { font: { size: 10 }, color: '#94a3b8' },
          border: { display: false },
        },
        y: {
          grid: { color: 'rgba(148, 163, 184, 0.12)', drawBorder: false },
          ticks: {
            callback: (v) => formatMoney(v, currency),
            font: { size: 10 },
            color: '#94a3b8',
            padding: 8,
          },
          border: { display: false },
        },
      },
    }),
    [currency]
  );

  // Invoice Status Donut (all-time)
  const status = data?.invoiceStatus || {
    paid: 0,
    unpaid: 0,
    overdue: 0,
    partial: 0,
  };
  const totalInvoices =
    safeNum(status.paid) +
    safeNum(status.unpaid) +
    safeNum(status.overdue) +
    safeNum(status.partial);
  const donutData = useMemo(
    () => ({
      labels: ['Paid', 'Unpaid', 'Overdue', 'Partial'],
      datasets: [
        {
          data: [
            safeNum(status.paid),
            safeNum(status.unpaid),
            safeNum(status.overdue),
            safeNum(status.partial),
          ],
          backgroundColor: ['#10b981', '#f59e0b', '#ef4444', '#3b82f6'],
          borderWidth: 3,
          borderColor: '#ffffff',
          hoverBorderWidth: 4,
        },
      ],
    }),
    [status]
  );
  const donutOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'bottom',
          labels: {
            boxWidth: 10,
            padding: 12,
            font: { size: 11, weight: '500' },
            usePointStyle: true,
            pointStyle: 'circle',
          },
        },
        tooltip: {
          backgroundColor: 'rgba(15, 23, 42, 0.95)',
          titleColor: '#f1f5f9',
          bodyColor: '#e2e8f0',
          borderColor: 'rgba(148, 163, 184, 0.2)',
          borderWidth: 1,
          padding: 12,
          displayColors: true,
          boxPadding: 4,
        },
      },
      cutout: '68%',
    }),
    []
  );

  // A/R Aging + DSO
  const aging = data?.arAging || {
    b0_30: 0,
    b31_60: 0,
    b61_90: 0,
    b90_plus: 0,
    dso: 0,
  };
  const agingLabels = ['0–30', '31–60', '61–90', '90+'];
  const agingValues = [
    aging.b0_30,
    aging.b31_60,
    aging.b61_90,
    aging.b90_plus,
  ].map((x) => safeNum(x));
  const arBarData = useMemo(
    () => ({
      labels: agingLabels,
      datasets: [
        {
          label: 'A/R',
          data: agingValues,
          backgroundColor: ['#86efac', '#93c5fd', '#fde68a', '#fca5a5'],
          borderWidth: 0,
          borderRadius: 6,
          barThickness: 28,
        },
      ],
    }),
    [agingValues]
  );
  const arBarOptions = useMemo(
    () => ({
      indexAxis: 'y',
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: 'rgba(15, 23, 42, 0.95)',
          titleColor: '#f1f5f9',
          bodyColor: '#e2e8f0',
          borderColor: 'rgba(148, 163, 184, 0.2)',
          borderWidth: 1,
          padding: 12,
          displayColors: false,
          callbacks: {
            label: (c) =>
              `${c.label}: ${formatMoney(c.parsed.x, currency)}`,
          },
        },
      },
      scales: {
        x: {
          grid: { color: 'rgba(148, 163, 184, 0.10)', drawBorder: false },
          ticks: {
            callback: (v) => formatMoney(v, currency),
            font: { size: 10 },
            color: '#94a3b8',
            padding: 8,
          },
          border: { display: false },
        },
        y: {
          grid: { display: false },
          ticks: {
            font: { size: 11, weight: '500' },
            color: '#475569',
            padding: 8,
          },
          border: { display: false },
        },
      },
    }),
    [currency]
  );

  const team = Array.isArray(data?.teamWorkload) ? data.teamWorkload : [];
  const expiringQuotes = data?.needsAttention?.expiringQuotes || [];
  const qsBase = toSearchParams(params);
  const partialErrors = Array.isArray(data?.errors) ? data.errors : [];
  const hasWarnings = partialErrors.length > 0;

  return (
    <div className="w-full min-h-[90vh] py-8 px-3 sm:px-6 lg:px-8 bg-slate-50">
      {/* Header Shell — single elevation */}
      <div className="rounded-2xl mb-6 bg-white shadow-sm">
        <div className="p-6 sm:p-7">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-slate-900">
                Dashboard
              </h1>
              <p className="mt-1 text-sm text-slate-600">
                Real-time overview of business performance
              </p>
              <div className="mt-2 flex flex-wrap items-center gap-1.5">
                {data?.freshness?.generatedAt ? (
                  <Chip tone="info">
                    Updated{' '}
                    {new Date(
                      data.freshness.generatedAt
                    ).toLocaleString(undefined, { timeZone: tz })}
                  </Chip>
                ) : null}
                {isFetching ? (
                  <Chip tone="default">
                    <span className="flex items-center gap-1">
                      <span className="inline-block h-1 w-1 animate-pulse rounded-full bg-slate-600"></span>
                      Refreshing
                    </span>
                  </Chip>
                ) : null}
              </div>
            </div>
            <div className="sm:ml-auto">
              <CompactTimeFilter
                params={params}
                helpers={filtersHelpers}
                loading={isFetching}
                onRefresh={refresh}
              />
            </div>
          </div>

          {hasWarnings && (
            <div className="mt-4 rounded-lg border border-amber-200/80 bg-amber-50/50 p-3 shadow-sm">
              <div className="flex items-start gap-2.5">
                <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-amber-200 text-amber-700 text-xs font-bold">
                  !
                </div>
                <div className="flex-1">
                  <div className="text-xs font-semibold text-amber-900">
                    Partial Data
                  </div>
                  <div className="mt-0.5 text-xs text-amber-800">
                    Some sections incomplete:
                    <span className="ml-2 inline-flex flex-wrap gap-1.5">
                      {partialErrors.map((e, i) => (
                        <Chip key={i} tone="default">
                          {e.scope || 'unknown'}
                        </Chip>
                      ))}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* KPI strip */}
      {isLoading ? (
        <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
          <Skeleton className="h-28" />
          <Skeleton className="h-28" />
          <Skeleton className="h-28" />
          <Skeleton className="h-28" />
          <Skeleton className="h-28" />
        </div>
      ) : isError ? (
        <div className="mb-8 rounded-2xl border border-rose-200/80 bg-rose-50/50 p-5 shadow-sm">
          <div className="flex items-start gap-3">
            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-rose-200 text-rose-700 text-sm font-bold">
              ✕
            </div>
            <div className="flex-1">
              <div className="text-sm font-semibold text-rose-900">
                Failed to Load Metrics
              </div>
              <p className="mt-1 text-xs text-rose-700">
                Unable to load dashboard metrics. Please refresh.
              </p>
              <button
                onClick={refresh}
                className="mt-3 rounded-md bg-rose-600 px-3 py-1.5 text-xs font-medium text-white transition-all hover:bg-rose-700"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
          {kpis.map((x, i) => (
            <KPI
              key={i}
              label={x.label}
              value={x.value}
              hint={x.hint}
              tone={x.tone}
              scope={x.scope}
              tooltip={x.tooltip}
            />
          ))}
          <JobsKPI scheduled={jobsScheduled} underway={jobsUnderway} />
        </div>
      )}

      {/* Charts Row */}
      <div className="mb-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Section
          title="Revenue Trend"
          scope="Period"
          tooltip="Booked = invoices issued per month; Collected = payments received per month. Both reflect the selected period window."
          right={
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1.5">
                <div className="h-2 w-2 rounded-full bg-indigo-500"></div>
                <span className="text-[11px] font-medium text-slate-500">
                  Booked
                </span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="h-2 w-2 rounded-full bg-emerald-500"></div>
                <span className="text-[11px] font-medium text-slate-500">
                  Collected
                </span>
              </div>
            </div>
          }
        >
          {isLoading ? (
            <Skeleton className="h-[320px]" />
          ) : trend.length === 0 ? (
            <div className="flex h-[320px] flex-col items-center justify-center rounded-2xl bg-slate-50/50 p-8 text-center">
              <div className="text-3xl opacity-20">📊</div>
              <div className="mt-3 text-sm font-medium text-slate-700">
                No Revenue Data
              </div>
              <p className="mt-1 max-w-sm text-xs text-slate-500">
                Revenue trends will appear once invoices and payments are
                recorded.
              </p>
            </div>
          ) : (
            <div className="h-[320px]">
              <Line data={lineData} options={lineOptions} />
            </div>
          )}
        </Section>

        <Section
          title="Invoice Status"
          scope="All-time"
          tooltip="All non-deleted invoices. Status is computed from balance and due date. Invoices without a due date are NOT considered overdue."
          right={
            <Link
              className="text-xs font-medium text-slate-600 transition-colors hover:text-slate-900"
              to={`/invoices?${toSearchParams(params)}`}
            >
              View All →
            </Link>
          }
        >
          {isLoading ? (
            <Skeleton className="h-[320px]" />
          ) : (
            <>
              <div className="relative mx-auto flex h-[260px] max-w-[360px] items-center justify-center">
                <Doughnut data={donutData} options={donutOptions} />
                {/* Center total */}
                <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                  <div className="text-2xl font-extrabold text-slate-900">
                    {totalInvoices}
                  </div>
                  <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                    Total
                  </div>
                </div>
              </div>
              {/* Only the two most operational counters */}
              <div className="mt-5 grid grid-cols-2 gap-3">
                <div className="rounded-lg border border-slate-200/60 bg-slate-50/50 p-3 text-center transition-all hover:border-slate-300">
                  <div className="text-xl font-bold tracking-tight text-slate-900">
                    {status.overdue}
                  </div>
                  <div className="mt-0.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                    Overdue
                  </div>
                </div>
                <div className="rounded-lg border border-slate-200/60 bg-slate-50/50 p-3 text-center transition-all hover:border-slate-300">
                  <div className="text-xl font-bold tracking-tight text-slate-900">
                    {status.unpaid}
                  </div>
                  <div className="mt-0.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                    Unpaid
                  </div>
                </div>
              </div>
            </>
          )}
        </Section>
      </div>

      {/* A/R Aging + DSO */}
      <div className="mb-6">
        <Section
          title="A/R Aging"
          scope="Snapshot"
          tooltip="Open receivables bucketed by days past due as of today. DSO (est.) = AR today ÷ (Booked in period ÷ days in period)."
          right={
            <div className="flex items-center gap-2">
              <span className="rounded-full bg-slate-900 px-2 py-0.5 text-[10px] font-semibold tracking-wider text-white">
                DSO {Math.round(safeNum(aging.dso))} days
              </span>
              <InfoTip
                text="Estimated Days Sales Outstanding based on current AR and booked invoices in the selected period."
                label="DSO info"
              />
            </div>
          }
        >
          {isLoading ? (
            <Skeleton className="h-[180px]" />
          ) : (
            <>
              <div className="h-[180px]">
                <Bar data={arBarData} options={arBarOptions} />
              </div>
              <div className="mt-5 grid grid-cols-4 gap-3">
                {agingLabels.map((label, i) => (
                  <div
                    key={label}
                    className="rounded-lg border border-slate-200/60 bg-slate-50/50 p-3 text-center transition-all hover:border-slate-300"
                  >
                    <div className="text-lg font-bold tracking-tight text-slate-900">
                      {formatMoney(agingValues[i], currency)}
                    </div>
                    <div className="mt-0.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                      {label} days
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </Section>
      </div>

      {/* Team & Alerts */}
      <div className="mb-6">
        <div className="mb-4 flex items-center gap-2">
          <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-slate-100 text-slate-600">
            <Activity className="h-4 w-4" />
          </div>
          <h2 className="text-lg font-semibold tracking-tight text-slate-900">
            Team & Alerts
          </h2>
        </div>

        <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
          <TeamTabs
            team={team}
            tz={tz}
            qsBase={toSearchParams(params)}
          />

          <Section
            title="Quotes Expiring Soon"
            scope="Snapshot"
            tooltip="Quotes in 'sent' status that will expire within the next 7 days from now. Not affected by the period filter."
            right={
              <div className="flex items-center gap-2">
                {expiringQuotes.length > 0 && (
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-amber-100 text-[10px] font-bold text-amber-700">
                    {expiringQuotes.length}
                  </span>
                )}
                <Link
                  className="text-xs font-medium text-slate-600 transition-colors hover:text-slate-900"
                  to={`/quote?${toSearchParams(params)}&expiring=7d`}
                >
                  View All →
                </Link>
                {expiringQuotes.length > 0 && (
                  <button
                    type="button"
                    onClick={() => exportExpiring(expiringQuotes)}
                    className="rounded-md border border-slate-200 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50"
                    title="Export CSV"
                  >
                    Export
                  </button>
                )}
              </div>
            }
          >
            {isLoading ? (
              <div className="space-y-3">
                <Skeleton className="h-14" />
                <Skeleton className="h-14" />
                <Skeleton className="h-14" />
              </div>
            ) : expiringQuotes.length === 0 ? (
              <div className="flex flex-col items-center justify-center rounded-2xl bg-slate-50/50 py-10 text-center">
                <div className="text-4xl opacity-15">📅</div>
                <div className="mt-3 text-sm font-medium text-slate-700">
                  No Expiring Quotes
                </div>
                <p className="mt-1 max-w-xs text-xs text-slate-500">
                  No quotes expiring in the next 7 days.
                </p>
              </div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {expiringQuotes.slice(0, 8).map((q) => (
                  <li
                    key={q.quoteId}
                    className="group flex items-center justify-between py-3.5 transition-colors hover:bg-slate-50/50"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="truncate text-sm font-semibold text-slate-900">
                          {q.quoteNumber}
                        </span>
                        <span className="text-slate-300">•</span>
                        <span className="truncate text-sm text-slate-600">
                          {q.clientName}
                        </span>
                      </div>
                      <div className="mt-1 flex items-center gap-3 text-[11px] text-slate-500">
                        <span className="flex items-center gap-1">
                          <span className="font-medium text-amber-600">
                            Expires
                          </span>
                          {q.expiresAt
                            ? new Date(q.expiresAt).toLocaleDateString(
                                undefined,
                                { timeZone: tz }
                              )
                            : '—'}
                        </span>
                        <span className="flex items-center gap-1">
                          <span className="font-medium">Amt</span>
                          {formatMoney(q.amount, currency)}
                        </span>
                      </div>
                    </div>
                    <Link
                      to={`/quote/${q.quoteId}`}
                      className="ml-4 shrink-0 rounded-md border border-slate-200/60 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 transition-all hover:border-slate-300 hover:bg-slate-50"
                    >
                      View →
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </Section>
        </div>
      </div>
    </div>
  );
}

/* ───────────────────────── CSV (no deps) ───────────────────────── */
function exportExpiring(rows) {
  downloadCsv(
    rows,
    [
      { header: 'Quote #', accessor: (r) => r.quoteNumber },
      { header: 'Client', accessor: (r) => r.clientName },
      {
        header: 'Expires At',
        accessor: (r) =>
          r.expiresAt ? new Date(r.expiresAt).toISOString() : '',
      },
      { header: 'Amount', accessor: (r) => r.amount },
    ],
    'expiring-quotes.csv'
  );
}

function downloadCsv(rows, columns, filename) {
  try {
    const header = columns
      .map((c) => `"${c.header.replace(/"/g, '""')}"`)
      .join(',');
    const body = rows
      .map((r) =>
        columns
          .map((c) => {
            const v = c.accessor(r);
            const s = v == null ? '' : String(v);
            return `"${s.replace(/"/g, '""')}"`;
          })
          .join(',')
      )
      .join('\n');
    const csv = `${header}\n${body}`;
    const blob = new Blob([csv], {
      type: 'text/csv;charset=utf-8;',
    });
    const url = URL.createObjectURL(blob);
    const a = Object.assign(document.createElement('a'), {
      href: url,
      download: filename,
    });
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  } catch {
    // ignore
  }
}
